import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import useAuth from "../../hooks/useAuth";
import { showToast, setCenterTab } from "../../store/uiSlice";
import { fmtUSD } from "../../utils/formatters";
import { getLotSize } from "../../utils/constants";
import { calcForexPnl } from "../../utils/forexUtils";
import ThemeToggle from "./ThemeToggle";
import api from "../../services/api";
import websocketService from "../../services/websocketService";

const getTagColors = (tag) => {
  const colors = {
    bronze: {
      bg: "var(--color-tagBronzeBg)",
      text: "var(--color-tagBronzeText)",
      border: "var(--color-tagBronzeBorder)",
    },
    silver: {
      bg: "var(--color-tagSilverBg)",
      text: "var(--color-tagSilverText)",
      border: "var(--color-tagSilverBorder)",
    },
    gold: {
      bg: "var(--color-tagGoldBg)",
      text: "var(--color-tagGoldText)",
      border: "var(--color-tagGoldBorder)",
    },
    diamond: {
      bg: "var(--color-tagDiamondBg)",
      text: "var(--color-tagDiamondText)",
      border: "var(--color-tagDiamondBorder)",
    },
    platinum: {
      bg: "var(--color-tagPlatinumBg)",
      text: "var(--color-tagPlatinumText)",
      border: "var(--color-tagPlatinumBorder)",
    },
    black: {
      bg: "var(--color-tagBlackBg)",
      text: "var(--color-tagBlackText)",
      border: "var(--color-tagBlackBorder)",
    },
  };
  return colors[tag] || colors.bronze;
};

const calcMarginLevel = (
  portfolio,
  marketData,
  balance,
  creditLimit,
  bonusBalance,
  totalCredit,
) => {
  const positions = Object.values(portfolio || {});
  if (positions.length === 0) return null;

  let totalMargin = 0;
  let openPnl = 0;

  positions.forEach((pos) => {
    const cost = Number(pos.cost) || 0;
    totalMargin += cost;
    const currentPrice =
      Number(marketData[pos.instrumentId]?.price) || Number(pos.avgPrice) || 0;
    const avgPrice = Number(pos.avgPrice) || 0;
    const qty = Math.abs(Number(pos.qty)) || 0;
    const cat = pos.category || "crypto";
    const isShort = pos.direction === "sell" || Number(pos.qty) < 0;
    const ls = getLotSize(cat, pos.instrumentId);
    openPnl += calcForexPnl(
      isShort,
      currentPrice,
      avgPrice,
      qty,
      pos.instrumentId,
      cat,
      ls,
    );
  });

  if (totalMargin === 0) return null;
  const equity =
    Number(balance) + openPnl + (totalCredit || 0) + (bonusBalance || 0);
  return { level: (equity / totalMargin) * 100, totalMargin, equity, openPnl };
};

const Topbar = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { logout } = useAuth();
  const { user } = useSelector((state) => state.auth);
  const { balance, portfolio, marketData } = useSelector(
    (state) => state.trade,
  );
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("Connected");
  const [isConnected, setIsConnected] = useState(true);
  const [copiedCode, setCopiedCode] = useState(false);
  const [totalCredit, setTotalCredit] = useState(0);

  const notifRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    try {
      websocketService.emitPageChange();
    } catch (e) {}
  }, [location.pathname]);

  useEffect(() => {
    const fetchCredit = async () => {
      try {
        const res = await api.get("/user/credit/requests");
        if (res.data && Array.isArray(res.data)) {
          const approved = res.data
            .filter((r) => r.status === "approved")
            .reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
          setTotalCredit(approved);
        }
      } catch (e) {}
    };
    fetchCredit();
  }, []);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await api.get("/notifications");
        let raw = response.data;
        if (!Array.isArray(raw)) raw = raw?.notifications ?? raw?.data ?? [];
        setNotifications(raw.slice(0, 10));
        setUnreadCount(raw.filter((n) => !n.read).length);
      } catch (error) {}
    };
    fetchNotifications();
    const interval = setInterval(() => {
      setIsConnected(true);
      setConnectionStatus("Connected");
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target))
        setShowNotifications(false);
      if (dropdownRef.current && !dropdownRef.current.contains(event.target))
        setShowUserDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      setNotifications(notifications.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
      dispatch(
        showToast({
          message: "All notifications marked as read",
          type: "success",
        }),
      );
    } catch (error) {}
  };

  const getUserInitials = () => {
    if (!user) return "U";
    return (
      (
        (user.firstName?.[0] || "") + (user.lastName?.[0] || "")
      ).toUpperCase() || "U"
    );
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "success":
        return "fa-circle-check";
      case "warn":
        return "fa-triangle-exclamation";
      case "error":
        return "fa-circle-exclamation";
      default:
        return "fa-circle-info";
    }
  };

  const tag = user?.tag || "bronze";
  const tradingAccountCode = user?.tradingAccountCode || user?.accountId || "—";
  const tagColors = getTagColors(tag);

  const copyTradingCode = (e) => {
    e.stopPropagation();
    navigator.clipboard
      .writeText(tradingAccountCode)
      .then(() => {
        setCopiedCode(true);
        dispatch(
          showToast({
            message: `Account ${tradingAccountCode} copied!`,
            type: "success",
            duration: 2000,
          }),
        );
        setTimeout(() => setCopiedCode(false), 2000);
      })
      .catch(() => {});
  };

  const creditLimit = Number(user?.creditLimit) || 0;
  const bonusBalance = Number(user?.bonusBalance) || 0;
  const marginData = calcMarginLevel(
    portfolio,
    marketData,
    balance,
    creditLimit,
    bonusBalance,
    totalCredit,
  );
  const marginLevel = marginData?.level ?? null;

  const getMarginStyle = () => {
    if (marginLevel === null || isNaN(marginLevel)) return {};
    if (marginLevel < 100) {
      return {
        siren: "animate-siren-red",
        dotColor: "bg-[#ef4444]",
        text: "text-[var(--color-text)]",
        icon: "fa-triangle-exclamation",
      };
    }
    if (marginLevel <= 200) {
      return {
        siren: "animate-siren-orange",
        dotColor: "bg-[#f59e0b]",
        text: "text-[var(--color-text)]",
        icon: "fa-circle-exclamation",
      };
    }
    return {
      siren: "animate-siren-green",
      dotColor: "bg-[#10b981]",
      text: "text-[var(--color-text)]",
      icon: "fa-shield-check",
    };
  };

  const ms = getMarginStyle();

  const formatMarginLevel = (level) => {
    if (level >= 1000000) return (level / 1000000).toFixed(1) + "M%";
    if (level >= 1000) return (level / 1000).toFixed(1) + "K%";
    return level.toFixed(1) + "%";
  };

  return (
    <div
      className={`${isMobile ? "fixed top-0 left-0 right-0" : ""} h-[52px] backdrop-blur-2xl flex items-center px-3 md:px-4 gap-2 md:gap-3 flex-shrink-0 relative`}
      style={{
        background: "var(--color-bg)",
        borderBottom: "1px solid var(--color-border)",
        zIndex: 99999,
      }}
    >
      {/* Brand Name - Desktop only */}
      <Link
        to="/terminal"
        className="hidden md:flex items-center flex-shrink-0"
      >
        <span
          className="font-syne text-xl font-extrabold tracking-tight"
          style={{ color: "var(--color-text)" }}
        >
          AnzoTrades
        </span>
      </Link>

      {/* Mobile Logo */}
      <Link
        to="/terminal"
        className="md:hidden flex items-center gap-2 flex-shrink-0"
      >
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center font-syne text-base font-extrabold"
          style={{
            background: "var(--color-gradientOrangePurple)",
            color: "var(--color-textInverse)",
            boxShadow: "0 0 18px var(--color-orangeGlow)",
          }}
        >
          A
        </div>
        <span
          className="font-syne text-lg font-extrabold"
          style={{ color: "var(--color-text)" }}
        >
          Anzo<span>Trade</span>
        </span>
      </Link>

      {/* Trading Account Code - Desktop only */}
      <div
        className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg cursor-pointer transition-all flex-shrink-0"
        style={{
          background: "var(--color-orangeBg)",
          border: "1px solid var(--color-orangeBorder)",
        }}
        onClick={copyTradingCode}
        title="Click to copy trading account number"
      >
        <i
          className="fas fa-id-card text-[10px]"
          style={{ color: "var(--color-text)" }}
        />
        <span
          className="text-[11px] font-mono font-extrabold tracking-wider"
          style={{ color: "var(--color-text)" }}
        >
          {tradingAccountCode}
        </span>
        {copiedCode ? (
          <i
            className="fas fa-check text-[9px]"
            style={{ color: "var(--color-green)" }}
          />
        ) : (
          <i
            className="fas fa-copy text-[9px]"
            style={{ color: "var(--color-text)", opacity: 0.7 }}
          />
        )}
      </div>

      <div
        className="hidden md:block w-px h-6"
        style={{ background: "var(--color-borderStrong)" }}
      />

      {/* Balance - Desktop only */}
      <div
        className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg flex-shrink-0"
        style={{
          border: "1px solid var(--color-borderHover)",
          background: "var(--color-bgSurfaceLight)",
        }}
      >
        <i
          className="fas fa-wallet text-[11px]"
          style={{ color: "var(--color-green)" }}
        />
        <span
          className="text-[9px] font-mono font-extrabold uppercase tracking-wider"
          style={{ color: "var(--color-textSecondary)" }}
        >
          Balance
        </span>
        <span
          className="text-[12px] font-mono font-extrabold"
          style={{ color: "var(--color-greenLight)" }}
        >
          {fmtUSD(balance)}
        </span>
      </div>

      {/* Margin Level - Desktop only */}
      {marginLevel !== null && !isNaN(marginLevel) && (
        <div
          className={`hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all flex-shrink-0 cursor-help ${ms.siren}`}
          style={{
            border: "1px solid var(--color-borderHover)",
            background: "var(--color-bgSurfaceLight)",
          }}
          title={`Margin Used: ${fmtUSD(marginData?.totalMargin || 0)}\nEquity: ${fmtUSD(marginData?.equity || 0)}\nOpen P&L: ${fmtUSD(marginData?.openPnl || 0)}`}
        >
          <i
            className={`fas ${ms.icon} text-[11px]`}
            style={{ color: "var(--color-text)" }}
          />
          <span
            className="text-[9px] font-mono font-extrabold uppercase tracking-wider"
            style={{ color: "var(--color-textSecondary)" }}
          >
            Margin Lvl
          </span>
          <span
            className={`text-[12px] font-mono font-extrabold`}
            style={{ color: "var(--color-text)" }}
          >
            {formatMarginLevel(marginLevel)}
          </span>
        </div>
      )}

      {/* Right side */}
      <div className="flex items-center gap-2 md:gap-3 ml-auto">
        {/* Deposit Button - Mobile only (dollar sign only) */}
        <Link
          to="/deposit"
          className="md:hidden flex items-center justify-center w-9 h-9 rounded-lg font-bold text-sm transition-all active:scale-95"
          style={{
            background: "var(--color-gradientOrange)",
            color: "var(--color-textInverse)",
            boxShadow: "0 0 14px var(--color-orangeGlow)",
            borderBottom: "2px solid var(--color-orangeDark)",
          }}
        >
          <i className="fas fa-dollar-sign text-[13px]" />
        </Link>

        {/* Analytics Button - Desktop only (hidden on mobile) */}
        <Link
          to="/analytics"
          className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg cursor-pointer transition-all flex-shrink-0"
          style={{
            background: "var(--color-purpleBg)",
            border: "1px solid var(--color-purpleBorder)",
          }}
          title="View Analytics"
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow =
              "0 0 12px var(--color-purpleGlow)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          <i
            className="fas fa-chart-pie text-[11px]"
            style={{ color: "var(--color-purpleLight)" }}
          />
          <span
            className="text-[11px] font-syne font-extrabold"
            style={{ color: "var(--color-purpleLight)" }}
          >
            Analytics
          </span>
        </Link>

        {/* Theme Toggle */}
        <ThemeToggle />

        {/* Notifications - Desktop only */}
        <div className="hidden md:block" ref={notifRef}>
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative w-9 h-9 rounded-lg flex items-center justify-center text-base transition-all"
            style={{
              background: "transparent",
              border: "1px solid var(--color-borderStrong)",
              color: "var(--color-textSecondary)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "var(--color-orange)";
              e.currentTarget.style.color = "var(--color-orange)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "var(--color-borderStrong)";
              e.currentTarget.style.color = "var(--color-textSecondary)";
            }}
          >
            <i className="fas fa-bell" />
            {unreadCount > 0 && (
              <span
                className="absolute -top-1 -right-1 w-4 h-4 bg-[#ef4444] rounded-full text-[9px] font-bold font-mono text-white flex items-center justify-center border-2"
                style={{ borderColor: "var(--color-bg)" }}
              >
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>
          {showNotifications && (
            <div
              className="fixed w-80 rounded-xl shadow-2xl animate-pop-in"
              style={{
                background: "var(--color-bgSurface)",
                border: "1px solid var(--color-border)",
                top: "52px",
                right: "100px",
                zIndex: 99999,
              }}
            >
              <div
                className="flex items-center justify-between px-4 py-3"
                style={{ borderBottom: "1px solid var(--color-borderMedium)" }}
              >
                <span
                  className="font-syne text-xs font-extrabold"
                  style={{ color: "var(--color-text)" }}
                >
                  <i className="fas fa-bell mr-1.5" /> Notifications
                </span>
                <button
                  onClick={markAllRead}
                  className="text-[10px] font-mono font-bold transition-colors"
                  style={{ color: "var(--color-textSecondary)" }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.color = "var(--color-orange)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = "var(--color-textSecondary)")
                  }
                >
                  Mark all read
                </button>
              </div>
              <div className="max-h-64 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="py-10 text-center">
                    <i
                      className="fas fa-bell-slash text-2xl mb-3 block"
                      style={{ color: "var(--color-textDim)" }}
                    />
                    <p
                      className="text-xs font-mono font-bold"
                      style={{ color: "var(--color-textSecondary)" }}
                    >
                      No notifications
                    </p>
                  </div>
                ) : (
                  notifications.map((notif) => (
                    <div
                      key={notif.id}
                      className={`px-4 py-3 flex gap-2.5 transition-colors`}
                      style={{
                        borderBottom: "1px solid var(--color-borderLight)",
                        background: notif.read
                          ? "transparent"
                          : "var(--color-orangeBg)",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background =
                          "var(--color-bgHover)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = notif.read
                          ? "transparent"
                          : "var(--color-orangeBg)")
                      }
                    >
                      <div
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-xs"
                        style={{
                          color:
                            notif.type === "success"
                              ? "var(--color-green)"
                              : notif.type === "warn"
                                ? "var(--color-orange)"
                                : notif.type === "error"
                                  ? "var(--color-red)"
                                  : "var(--color-purple)",
                          background:
                            notif.type === "success"
                              ? "var(--color-greenBg)"
                              : notif.type === "warn"
                                ? "var(--color-orangeBg)"
                                : notif.type === "error"
                                  ? "var(--color-redBg)"
                                  : "var(--color-purpleBg)",
                        }}
                      >
                        <i
                          className={`fas ${getNotificationIcon(notif.type)}`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div
                          className="text-[11px] font-bold leading-tight"
                          style={{ color: "var(--color-text)" }}
                        >
                          {notif.message}
                        </div>
                        <div
                          className="text-[9px] font-mono font-bold mt-1"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          {notif.time}
                        </div>
                      </div>
                      {!notif.read && (
                        <div
                          className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                          style={{ background: "var(--color-orange)" }}
                        />
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Connection Status - Desktop only */}
        <div
          className="hidden md:flex items-center gap-2 px-2.5 py-1.5 rounded-full"
          style={{
            background: "var(--color-greenBg)",
            border: "1px solid var(--color-greenBorder)",
          }}
        >
          <span
            className={`w-2 h-2 rounded-full ${isConnected ? "animate-pulse" : ""}`}
            style={{
              background: isConnected
                ? "var(--color-statusConnected)"
                : "var(--color-statusDisconnected)",
            }}
          />
          <span
            className="text-[11px] font-mono font-bold whitespace-nowrap"
            style={{ color: "var(--color-green)" }}
          >
            {connectionStatus}
          </span>
        </div>

        {/* User Avatar + Dropdown */}
        <div
          className="flex items-center gap-2 pl-2"
          style={{ borderLeft: "1px solid var(--color-borderStrong)" }}
          ref={dropdownRef}
        >
          <div
            onClick={() => setShowUserDropdown(!showUserDropdown)}
            className="flex items-center gap-2 cursor-pointer"
          >
            <div className="hidden md:block text-right">
              <div
                className="font-syne text-xs font-extrabold flex items-center gap-1.5"
                style={{ color: "var(--color-text)" }}
              >
                {user?.firstName || "Guest"}
                <span
                  className="text-[8px] px-2 py-0.5 rounded font-mono font-bold border"
                  style={{
                    background: tagColors.bg,
                    color: tagColors.text,
                    borderColor: tagColors.border,
                  }}
                >
                  {tag.charAt(0).toUpperCase() + tag.slice(1)}
                </span>
              </div>
            </div>
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center font-syne text-[11px] font-extrabold"
              style={{
                background: "var(--color-gradientOrangePurple)",
                color: "var(--color-textInverse)",
                boxShadow: "0 0 12px var(--color-orangeGlow)",
              }}
            >
              {getUserInitials()}
            </div>
            <i
              className={`fas fa-chevron-down text-[10px] transition-transform duration-200 ${showUserDropdown ? "rotate-180" : ""}`}
              style={{ color: "var(--color-textSecondary)" }}
            />
          </div>
          {showUserDropdown && (
            <div
              className="fixed w-60 rounded-xl shadow-2xl animate-pop-in overflow-hidden"
              style={{
                background: "var(--color-bgSurface)",
                border: "1px solid var(--color-border)",
                top: "52px",
                right: "16px",
                zIndex: 99999,
              }}
            >
              <div
                className="px-4 py-3 border-b"
                style={{
                  background:
                    "linear-gradient(to right, var(--color-orangeBg), var(--color-purpleBg))",
                  borderColor: "var(--color-borderMedium)",
                }}
              >
                <div
                  className="font-syne text-sm font-extrabold flex items-center gap-1.5"
                  style={{ color: "var(--color-text)" }}
                >
                  {user?.firstName} {user?.lastName}
                  <span
                    className="text-[8px] px-2 py-0.5 rounded font-mono font-bold border"
                    style={{
                      background: tagColors.bg,
                      color: tagColors.text,
                      borderColor: tagColors.border,
                    }}
                  >
                    {tag.charAt(0).toUpperCase() + tag.slice(1)}
                  </span>
                </div>
                <div
                  className="text-[10px] font-mono font-bold mt-1 flex items-center gap-2"
                  style={{ color: "var(--color-textSecondary)" }}
                >
                  <span>Trading Account</span>
                  <span
                    className="font-extrabold cursor-pointer transition-colors"
                    style={{ color: "var(--color-orange)" }}
                    onClick={copyTradingCode}
                  >
                    {tradingAccountCode}
                  </span>
                </div>
              </div>
              <div
                className="text-[9px] font-mono font-extrabold tracking-[0.1em] px-4 pt-2.5 pb-1.5 uppercase"
                style={{ color: "var(--color-textSecondary)" }}
              >
                Account
              </div>
              {[
                { to: "/terminal", icon: "fa-chart-line", label: "Terminal" },
                { to: "/home", icon: "fa-house", label: "Home" },
                { to: "/analytics", icon: "fa-chart-pie", label: "Analytics" },
                { to: "/profile", icon: "fa-user-circle", label: "Profile" },
                {
                  to: "/change-password",
                  icon: "fa-lock",
                  label: "Change Password",
                },
                {
                  to: "/documents",
                  icon: "fa-file-shield",
                  label: "Documents",
                },
              ].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setShowUserDropdown(false)}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-[11px] font-bold transition-colors"
                  style={{ color: "var(--color-textSecondary)" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "var(--color-bgHover)";
                    e.currentTarget.style.color = "var(--color-text)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = "var(--color-textSecondary)";
                  }}
                >
                  <i className={`fas ${item.icon} w-4 text-center text-xs`} />{" "}
                  {item.label}
                </Link>
              ))}
              <button
                onClick={() => {
                  window.dispatchEvent(new CustomEvent("open-questionnaire"));
                  setShowUserDropdown(false);
                }}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[11px] font-bold text-left transition-colors"
                style={{ color: "var(--color-textSecondary)" }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "var(--color-bgHover)";
                  e.currentTarget.style.color = "var(--color-text)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = "var(--color-textSecondary)";
                }}
              >
                <i className="fas fa-clipboard-list w-4 text-center text-xs" />{" "}
                Questionnaire
              </button>
              <div
                className="h-px my-1"
                style={{ background: "var(--color-borderMedium)" }}
              />
              <div
                className="text-[9px] font-mono font-extrabold tracking-[0.1em] px-4 pt-2.5 pb-1.5 uppercase"
                style={{ color: "var(--color-textSecondary)" }}
              >
                Finance
              </div>
              {[
                { to: "/deposit", icon: "fa-circle-plus", label: "Deposit" },
                {
                  to: "/withdrawal",
                  icon: "fa-circle-minus",
                  label: "Withdrawal",
                },
                { to: "/credit", icon: "fa-credit-card", label: "Credit" },
                {
                  to: "/statement",
                  icon: "fa-file-invoice-dollar",
                  label: "Statement",
                },
              ].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setShowUserDropdown(false)}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-[11px] font-bold transition-colors"
                  style={{ color: "var(--color-textSecondary)" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "var(--color-bgHover)";
                    e.currentTarget.style.color = "var(--color-text)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = "var(--color-textSecondary)";
                  }}
                >
                  <i className={`fas ${item.icon} w-4 text-center text-xs`} />{" "}
                  {item.label}
                </Link>
              ))}
              <div
                className="h-px my-1"
                style={{ background: "var(--color-borderMedium)" }}
              />
              <button
                onClick={() => {
                  logout();
                  setShowUserDropdown(false);
                }}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[11px] font-bold transition-colors"
                style={{ color: "var(--color-red)" }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "var(--color-redBg)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                <i className="fas fa-right-from-bracket w-4 text-center text-xs" />{" "}
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pop-in{from{opacity:0;transform:scale(0.93)}to{opacity:1;transform:scale(1)}}.animate-pop-in{animation:pop-in 0.2s ease}
        @keyframes siren-red{0%,100%{box-shadow:0 0 8px rgba(239,68,68,0.4),0 0 16px rgba(239,68,68,0.2),inset 0 0 8px rgba(239,68,68,0.1)}25%{box-shadow:0 0 20px rgba(239,68,68,0.9),0 0 40px rgba(239,68,68,0.5),0 0 60px rgba(239,68,68,0.25),inset 0 0 16px rgba(239,68,68,0.15)}50%{box-shadow:0 0 8px rgba(239,68,68,0.4),0 0 16px rgba(239,68,68,0.2),inset 0 0 8px rgba(239,68,68,0.1)}75%{box-shadow:0 0 20px rgba(239,68,68,0.9),0 0 40px rgba(239,68,68,0.5),0 0 60px rgba(239,68,68,0.25),inset 0 0 16px rgba(239,68,68,0.15)}}.animate-siren-red{animation:siren-red 0.7s ease-in-out infinite}
        @keyframes siren-orange{0%,100%{box-shadow:0 0 6px rgba(245,158,11,0.3),0 0 12px rgba(245,158,11,0.15),inset 0 0 6px rgba(245,158,11,0.08)}30%{box-shadow:0 0 16px rgba(245,158,11,0.7),0 0 32px rgba(245,158,11,0.35),0 0 48px rgba(245,158,11,0.18),inset 0 0 12px rgba(245,158,11,0.12)}60%{box-shadow:0 0 6px rgba(245,158,11,0.3),0 0 12px rgba(245,158,11,0.15),inset 0 0 6px rgba(245,158,11,0.08)}}.animate-siren-orange{animation:siren-orange 1.4s ease-in-out infinite}
        @keyframes siren-green{0%,100%{box-shadow:0 0 4px rgba(16,185,129,0.2),0 0 8px rgba(16,185,129,0.1),inset 0 0 4px rgba(16,185,129,0.05)}50%{box-shadow:0 0 10px rgba(16,185,129,0.4),0 0 20px rgba(16,185,129,0.15),inset 0 0 8px rgba(16,185,129,0.08)}}.animate-siren-green{animation:siren-green 3s ease-in-out infinite}
      `}</style>
    </div>
  );
};

export default Topbar;
